/* import __COLOCATED_TEMPLATE__ from './dispute-form-modal.hbs'; */
import { getDate } from '@banking/ui/models/checking/balances/unified-activity';
import formatMoney from '@banking/ui/utils/format-money';
import Component from '@ember/component';
import { computed } from '@ember/object';
import { or, readOnly } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import Money from '@square/dashboard-money/models/money';
import { ComponentKind, InputKind } from '@square/dashboard-protos/models/squareup/jedi/service';
import { getCookie } from '@square/dashboard-utilities/utils/cookies';
import { feature } from '@square/ember-features/macros';

// eslint-disable-next-line ember/no-classic-classes
export default Component.extend({
  ComponentKind,
  formatter: service(),
  getPanel: service('jedi/get-panel'),
  intl: service(),
  localeTime: service(),
  router: service(),
  startSession: service('jedi/start-session'),
  toasterService: service('market-toasts'),
  multiPaymentFlowEnabled: feature('disputes/use_square_card_multipayment'),
  amount: readOnly('activity.amount'),
  merchantName: or('activity.title', 'activity.description'),
  merchantImageUrl: readOnly('activity.merchantImage.merchantImageUrl'),
  transactionToken: readOnly('activity.transactionToken'),
  activity: null,
  additionalPayments: null,
  isFraudDispute: null,
  isLoading: true,
  isOriginalPaymentSelected: true,
  sessionToken: null,
  showMultiPaymentSelectModal: false,
  showSubmissionError: false,
  showUnsavedChangesModal: false,
  unitToken: null,
  init(...args) {
    this._super(...args);
    this.setProperties({
      onFraudQuestionPanel: this.multiPaymentFlowEnabled
    });
    this.startSession.makeStartSessionRequest({
      anonymousToken: this.anonymousToken,
      unitToken: this.unitToken
    }).then(sessionResponse => {
      this.setProperties({
        sessionToken: sessionResponse.sessionToken
      });
      this.setPanel({
        panelToken: 'F0355A53800F'
      });
    });
  },
  setPanel(params) {
    this.setProperties({
      isLoading: true,
      showSubmissionError: false
    });
    this.getPanel.makeGetPanelRequest(Object.assign(params, {
      sessionToken: this.sessionToken,
      anonymousToken: this.anonymousToken
    })).then(panelResponse => {
      let panelSubmitted = false;
      let beforeReviewPageRender = false;
      panelResponse.panel.components.forEach(component => {
        const answer = this.inputs.findBy('name', component.name)?.value;
        if (component.kind === ComponentKind.RESOLUTION) {
          panelSubmitted = true;
        } else if (answer) {
          this.setComponentValue(component, answer);
        }
        if (component.name === 'submit') {
          beforeReviewPageRender = true;
        }
      });

      // We need to filter out transaction date, merchant, and amount components from the review
      // page. Since Jedi doesn't return a name attribute for these components, we use the components
      // return indices to achieve this. By doing this, we assume that they are returned consistently in
      // the same order: transaction date, merchant, and amount are indexed as 3, 4 and 5, respectively.
      if (beforeReviewPageRender) {
        panelResponse.panel.components = panelResponse.panel.components.filter((_, index) => index !== 3 && index !== 4 && index !== 5);
      }
      if (this.onReviewPage && panelSubmitted) {
        if (panelResponse.success) {
          this.router.transitionTo('square-card.disputes');
          this.toasterService.success(this.disputeSubmittedToast, {
            persistent: false,
            showDismiss: false
          });
        } else {
          this.setProperties({
            showSubmissionError: true
          });
        }
      } else {
        this.setProperties({
          panel: panelResponse.panel
        });
      }
      if (this.multiPaymentFlowEnabled && this.onFraudQuestionPanel || !this.multiPaymentFlowEnabled && this.onFirstJediPanel) {
        this.updateInput('transaction_date', this.transactionDate);
        this.updateInput('merchant', this.merchantName);
        this.updateInput('amount', (Math.abs(this.amount.amount) / 100).toString());
      }
      this.setProperties({
        isLoading: false
      });
    });
  },
  setComponentValue(component, answer) {
    if (answer && component) {
      if (component.kind === ComponentKind.SINGLE_SELECT_LIST) {
        component.parameters.selected_value = answer;
      } else {
        component.parameters.text = answer;
      }
    }
  },
  updateInput(name, value) {
    const input = this.inputs.findBy('name', name);
    const component = this.panel.components.findBy('name', name);
    if (input) {
      this.inputs.removeObject(input);
    }
    this.inputs.pushObject({
      kind: InputKind.TEXT,
      name,
      value
    });
    this.setComponentValue(component, value);
  },
  saveAdditionalPayments(payments) {
    this.setProperties({
      additionalPayments: payments ? payments.sort((a, b) => {
        if (a.availableAt) {
          return b.availableAt - a.availableAt;
        } else {
          return b.latestEventAt.instantUsec - a.latestEventAt.instantUsec;
        }
      }) : null
    });
  },
  saveOriginalPaymentSelection(isOriginalPaymentSelected) {
    this.setProperties({
      isOriginalPaymentSelected
    });
  },
  updateInputsOfAdditionalPayments() {
    // If original payment is not selected, take the first additional payment as original
    if (!this.isOriginalPaymentSelected) {
      const newOriginalPayment = this.additionalPayments[0];
      this.updateInput('transaction_token', newOriginalPayment.transactionToken);
      this.updateInput('transaction_date', newOriginalPayment.lineItems.firstObject.timestamp.value || getDate(newOriginalPayment.latestEventAt, this.localeTime));
      this.updateInput('merchant', newOriginalPayment.title || newOriginalPayment.description);
      this.updateInput('amount', (Math.abs(newOriginalPayment.amount.amount) / 100).toString());
      this.updateInputsOfAdditionalPaymentsGivenStartIndex(1);
    } else {
      this.updateInputsOfAdditionalPaymentsGivenStartIndex(0);
    }
    this.updateInput('num_transactions', (this.additionalPayments.length + (this.isOriginalPaymentSelected ? 1 : 0)).toString());
  },
  updateInputsOfAdditionalPaymentsGivenStartIndex(startIndex) {
    let index = 1;
    for (let i = startIndex; i < this.additionalPayments.length; i++) {
      const payment = this.additionalPayments[i];
      this.updateInput(`transaction_token_${index}`, payment.transactionToken);
      let transactionDate;
      if (payment.availableAt) {
        transactionDate = this.intl.formatDate(payment.availableAt, {
          format: 'fullDateLong'
        });
      } else {
        transactionDate = getDate(payment.latestEventAt, this.localeTime);
      }
      this.updateInput(`transaction_date_${index}`, transactionDate);
      this.updateInput(`merchant_${index}`, payment.title || payment.description);
      this.updateInput(`amount_${index}`, (Math.abs(payment.amount.amount) / 100).toString());
      index++;
    }
  },
  anonymousToken: computed(function () {
    return getCookie('_savt');
  }),
  disputedPaymentsDescription: computed('additionalPayments', 'amount.currency', 'isFraudDispute', 'isOriginalPaymentSelected', 'merchantName', 'transactionDate', function () {
    const disputedPaymentsDescription = [];
    if (this.isOriginalPaymentSelected) {
      disputedPaymentsDescription.push(this.intl.t('disputes.squareCardClaims.multiPaymentSelectModal.disputedPayments.description', {
        merchantName: this.merchantName,
        amount: this.amount.currency ? formatMoney(this.amount, this.formatter) : Money.fromProto(this.amount).unsigned.get('formatted'),
        date: this.transactionDate
      }));
    }
    if (this.isFraudDispute && this.additionalPayments) {
      this.additionalPayments.forEach(payment => {
        disputedPaymentsDescription.push(this.intl.t('disputes.squareCardClaims.multiPaymentSelectModal.disputedPayments.description', {
          merchantName: payment.title || payment.description,
          amount: payment.amount.currency ? formatMoney(payment.amount, this.formatter) : Money.fromProto(payment.amount).unsigned.get('formatted'),
          date: payment.availableAt ? this.intl.formatDate(payment.availableAt, {
            format: 'fullDateLong'
          }) : getDate(payment.latestEventAt, this.localeTime)
        }));
      });
    }
    return disputedPaymentsDescription;
  }),
  disputedPaymentsLabel: computed('totalSelectedPayments', function () {
    if (this.totalSelectedPayments > 1) {
      return this.intl.t('disputes.squareCardClaims.disputeFormModal.disputedPayments.label.other');
    } else {
      return this.intl.t('disputes.squareCardClaims.disputeFormModal.disputedPayments.label.one');
    }
  }),
  disputeSubmittedToast: computed('totalSelectedPayments', function () {
    if (this.totalSelectedPayments > 1) {
      return this.intl.t('disputes.squareCardClaims.disputeFormModal.success.disputeSubmitted.other');
    } else {
      return this.intl.t('disputes.squareCardClaims.disputeFormModal.success.disputeSubmitted.one');
    }
  }),
  inputs: computed(function () {
    return [];
  }),
  isNextButtonDisabled: computed('isLoading', 'isFraudDispute', 'onFraudQuestionPanel', 'panel.components.@each.{name,parameters}', 'inputs.@each.{name,value}', function () {
    if (this.isLoading) {
      return true;
    }
    if (this.onFraudQuestionPanel) {
      return this.isFraudDispute === null;
    }
    return !this.panel?.components.every(component => {
      if (component.parameters.required === 'true') {
        const input = this.inputs.findBy('name', component.name);
        if (input && input.value.length > 0) {
          return true;
        } else {
          return false;
        }
      }
      return true;
    });
  }),
  onFirstJediPanel: computed('onFraudQuestionPanel', 'panel.components.@each.kind', function () {
    return !this.onFraudQuestionPanel && !this.panel?.components.some(component => component.kind === ComponentKind.BACK);
  }),
  onReviewPage: computed('panel.components.@each.name', function () {
    return this.panel?.components?.some(component => component.name === 'submit');
  }),
  showBackButton: computed('onFirstJediPanel', 'onFraudQuestionPanel', 'multiPaymentFlowEnabled', function () {
    return !this.onFirstJediPanel && !this.onFraudQuestionPanel || this.onFirstJediPanel && this.multiPaymentFlowEnabled;
  }),
  totalSelectedPayments: computed('isOriginalPaymentSelected', 'additionalPayments.length', function () {
    return (this.isOriginalPaymentSelected ? 1 : 0) + (this.additionalPayments || []).length;
  }),
  transactionDate: computed('activity.{latestEventAt,lineItems.firstObject.timestamp.value}', function () {
    if (this.activity.lineItems) {
      return this.activity.lineItems.firstObject.timestamp.value;
    } else {
      return getDate(this.activity.latestEventAt, this.localeTime);
    }
  }),
  // eslint-disable-next-line ember/no-actions-hash
  actions: {
    getNextPanel() {
      if (this.onFraudQuestionPanel) {
        this.setProperties({
          onFraudQuestionPanel: false
        });
      } else {
        if (this.onReviewPage) {
          if (this.isOriginalPaymentSelected) {
            this.updateInput('transaction_token', this.transactionToken);
          }
          if (this.isFraudDispute && this.additionalPayments) {
            this.updateInputsOfAdditionalPayments();
          }
        }
        this.setPanel({
          inputs: this.inputs
        });
      }
    },
    getPreviousPanel() {
      if (this.onFirstJediPanel) {
        this.setProperties({
          onFraudQuestionPanel: true
        });
      } else {
        this.setPanel({
          inputs: [{
            kind: InputKind.BUTTON_PRESS,
            name: 'back',
            value: 'true'
          }]
        });
      }
    },
    updateFraudDisputeSelection(value) {
      this.setProperties({
        isFraudDispute: value === 'true'
      });
    },
    updateInput(name, event) {
      this.updateInput(name, event?.detail?.value || event?.detail?.newSelectionValue);
    },
    updateDateInput(name, event) {
      this.updateInput(name, this.localeTime.dateTime(event, 'longDate'));
    }
  }
});